import { useMemo } from "react";
import { Badge, Button, DropdownMenu, Flex, Text } from "@radix-ui/themes";
import { ChevronDown, Home, Plus, Share2, Shield, Star, User } from "lucide-react";
import { Dashboard } from "@/types/breeze/Dashboard";

interface Props {
  dashboards: Dashboard[];
  currentId: number;
  currentUserId: number;
  onSelect: (dashboardId: number) => void;
  onCreateNew: () => void;
}

/**
 * Groups the visible dashboards into: tenant default, my dashboards, shared by others.
 * Clicking an entry navigates to it; "New dashboard" at the bottom opens the create modal.
 */
export function DashboardSwitcher({
  dashboards,
  currentId,
  currentUserId,
  onSelect,
  onCreateNew,
}: Props) {
  const { tenantDefault, mine, sharedByOthers } = useMemo(() => {
    const tenantDefault = dashboards.find((d) => d.is_default);
    const mine = dashboards
      .filter((d) => !d.is_default && d.owner_user_id === currentUserId)
      .sort((a, b) => Number(b.is_home) - Number(a.is_home) || a.name.localeCompare(b.name));
    const sharedByOthers = dashboards
      .filter((d) => !d.is_default && d.owner_user_id !== currentUserId && d.is_shared)
      .sort((a, b) => a.name.localeCompare(b.name));
    return { tenantDefault, mine, sharedByOthers };
  }, [dashboards, currentUserId]);

  const current = dashboards.find((d) => d.id === currentId);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button variant="soft" style={{ fontWeight: "normal", minWidth: 200, justifyContent: "space-between" }}>
          <Flex align="center" gap="2">
            {current?.is_home && <Star size={14} fill="currentColor" />}
            {current?.is_default && !current?.owner_user_id && <Shield size={14} />}
            <Text truncate>{current?.name ?? "Dashboard"}</Text>
            {current?.is_shared && (
              <Badge size="1" color="green" variant="soft">
                <Share2 size={10} /> {current.allow_shared_edit ? "Shared • editable" : "Shared"}
              </Badge>
            )}
          </Flex>
          <ChevronDown size={14} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content style={{ minWidth: 260 }}>
        {tenantDefault && (
          <>
            <DropdownMenu.Label>Project</DropdownMenu.Label>
            <DropdownMenu.Item onSelect={() => onSelect(tenantDefault.id)}>
              <Flex align="center" gap="2" width="100%" justify="between">
                <Flex align="center" gap="2">
                  <Shield size={14} />
                  <Text>{tenantDefault.name}</Text>
                </Flex>
                {tenantDefault.id === currentId && <Badge size="1" color="gray">current</Badge>}
              </Flex>
            </DropdownMenu.Item>
          </>
        )}

        {mine.length > 0 && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Label>My dashboards</DropdownMenu.Label>
            {mine.map((d) => (
              <DropdownMenu.Item key={d.id} onSelect={() => onSelect(d.id)}>
                <Flex align="center" gap="2" width="100%" justify="between">
                  <Flex align="center" gap="2">
                    {d.is_home ? <Star size={14} fill="currentColor" /> : <Home size={14} />}
                    <Text>{d.name}</Text>
                    {d.is_shared && (
                      <Badge size="1" color="green" variant="soft">
                        <Share2 size={10} /> {d.allow_shared_edit ? "Shared • editable" : "Shared"}
                      </Badge>
                    )}
                  </Flex>
                  {d.id === currentId && <Badge size="1" color="gray">current</Badge>}
                </Flex>
              </DropdownMenu.Item>
            ))}
          </>
        )}

        {sharedByOthers.length > 0 && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Label>Shared with you</DropdownMenu.Label>
            {sharedByOthers.map((d) => (
              <DropdownMenu.Item key={d.id} onSelect={() => onSelect(d.id)}>
                <Flex align="center" gap="2" width="100%" justify="between">
                  <Flex align="center" gap="2">
                    <User size={14} />
                    <Text>{d.name}</Text>
                    <Badge size="1" color="green" variant="soft">
                      <Share2 size={10} /> {d.allow_shared_edit ? "Shared • editable" : "Shared"}
                    </Badge>
                  </Flex>
                  {d.id === currentId && <Badge size="1" color="gray">current</Badge>}
                </Flex>
              </DropdownMenu.Item>
            ))}
          </>
        )}

        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={onCreateNew}>
          <Flex align="center" gap="2">
            <Plus size={14} />
            <Text>New dashboard</Text>
          </Flex>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
